import { useEffect, useMemo, useState } from 'react'
import FormInput from '../../components/atoms/Input/Form-Input'
import Button from '../../components/atoms/Buttons'
import ImagePlus from '../../assets/icons/ImagePlus.svg?react'
import CloseIcon from '../../assets/icons/close-icon.svg?react'
import { FaPlus } from 'react-icons/fa6'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { CREATE_BUSINESS_FORM } from '../../apollo/mutations/createForm'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_MANUFACTURE } from '../../apollo/queries/getManufacture'
import useManufacturerBrandingUpload from '../../hooks/useManufacturerBrandingUpload'
import { useUserContext } from '../../context/UserContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { UPDATE_USER } from '../../apollo/mutations/updateUser'
import { createToast } from '../../utils/Toast'
import { fetchAuthSession } from 'aws-amplify/auth'
import { formatName } from '../../utils'
// import { ClientPhoneIcon } from '../../Icons'

const Toast = createToast()
const MAX_IMAGE_SIZE = 5 * 1024 * 1024

const OnBoardingForm = ({ onStepChange }) => {
  const brand = localStorage.getItem('brand') || ''
  const { userData } = useUserContext()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const editMode = params.get('editMode') === 'true'

  const parsedFormData = useMemo(() => {
    try {
      if (editMode && userData?.manufacturerObject?.businessForm) {
        return JSON.parse(userData.manufacturerObject.businessForm)
      }
    } catch (err) {
      console.error(err)
    }
    return null
  }, [editMode, userData?.manufacturerObject?.businessForm])

  const methods = useForm({
    defaultValues: parsedFormData || {
      companyName: '',
      legalName: '',
      companyEmail: '',
      companyAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      legalBusinessAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      subsidiaryBrands: [''],
      officePhoneNumber: '',
      hoursOfOperation: {
        monday: { open: '', close: '', closed: false },
        tuesday: { open: '', close: '', closed: false },
        wednesday: { open: '', close: '', closed: false },
        thursday: { open: '', close: '', closed: false },
        friday: { open: '', close: '', closed: false },
        saturday: { open: '', close: '', closed: false },
        sunday: { open: '', close: '', closed: false },
      },
      contacts: {
        ownership: [
          { firstName: '', lastName: '', email: '', phone: '', title: '' },
        ],
        technical: [
          { firstName: '', lastName: '', email: '', phone: '', title: '' },
        ],
        accountsPayable: [
          { firstName: '', lastName: '', email: '', phone: '', title: '' },
        ],
        accountsReceivable: [
          { firstName: '', lastName: '', email: '', phone: '', title: '' },
        ],
      },
      shippingAddress: {
        shipTo: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
        shipFrom: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
      },
      saddleTypes: [],
      otherSaddleType: '',
      leadTimes: {
        production: '',
        custom: '',
      },
      policies: {
        shipsQuickly: '',
        acceptsReturns: '',
        returnsTerms: '',
        hasWarranty: '',
        warrantyTerms: '',
        allowsCancellation: '',
        cancellationTerms: '',
      },
      branding: {
        logoFile: null,
        mediaFiles: [],
      },
    },
  })

  useEffect(() => {
    if (editMode && parsedFormData) {
      methods.reset(parsedFormData)
      if (parsedFormData.branding.logoUrl) {
        setLogoUrl(parsedFormData.branding.logoUrl)
        methods.setValue('branding.logoUrl', parsedFormData.branding.logoUrl)
      }

      if (parsedFormData.branding.mediaUrls?.length > 0) {
        const previews = parsedFormData.branding.mediaUrls.map(url => ({
          url,
          isUploaded: true,
          type: url.match(/\.(mp4|webm)$/i) ? 'video' : 'image',
        }))
        setMediaPreviews(previews)
        methods.setValue(
          'branding.mediaUrls',
          parsedFormData.branding.mediaUrls,
        )
      }
    }
  }, [editMode, parsedFormData, methods])

  const {
    uploading,
    progress,
    errors: uploadErrors,
    uploadFile,
    acceptedTypes,
  } = useManufacturerBrandingUpload()

  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER)
  const [createBusinessForm, { loading: createManufactureLoading }] =
    useMutation(CREATE_BUSINESS_FORM)
  const [getManufacture] = useLazyQuery(GET_MANUFACTURE)

  const { handleSubmit, control, setValue, getValues, watch, formState } =
    methods
  const branding = watch('branding')
  const [loading, setLoading] = useState(false)
  const [logoUrl, setLogoUrl] = useState(null)
  const [mediaUrls, setMediaUrls] = useState([])
  const [mediaPreviews, setMediaPreviews] = useState([])

  const saddleDisciplines = [
    'Barrel Racing',
    'Cutting',
    'Ranch Riding',
    'Reining',
    'Roping',
    'Team Penning Sorting',
    'Trail Riding',
    'Endurance',
    'Western Pleasure',
    'Other',
  ]

  const leadTimeOptions = [
    '1-2 weeks',
    '2-4 weeks',
    '4-6 weeks',
    '6-8 weeks',
    '8-12 weeks',
    '12-16 weeks',
    '16+ weeks',
  ]

  const handleArrayChange = (fieldName, index, value) => {
    const array = getValues(fieldName)
    array[index] = value
    setValue(fieldName, array)
  }

  const addArrayItem = (fieldName, defaultValue) => {
    const array = getValues(fieldName) || []
    setValue(fieldName, [...array, defaultValue])
  }

  const removeArrayItem = (fieldName, index) => {
    const array = getValues(fieldName)
    setValue(
      fieldName,
      array.filter((_, i) => i !== index),
    )
  }

  const validateSaddleTypes = value => {
    return value.length > 0 || 'Please select at least one saddle type'
  }

  const validateOtherSaddleType = value => {
    const saddleTypes = getValues('saddleTypes')
    return (
      !saddleTypes.includes('Other') ||
      (value && value.trim() !== '') ||
      'Please specify other saddle type'
    )
  }

  const validateHoursOfOperation = hours => {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ]
    const hasAtLeastOneOpenDay = days.some(day => {
      const dayHours = hours[day]
      return !dayHours.closed && (dayHours.open || dayHours.close)
    })

    return (
      hasAtLeastOneOpenDay ||
      'Please specify operating hours for at least one day'
    )
  }

  const handlePhoneNumberChange = event => {
    const input = event.target
    const rawInput = input.value
    const cursorPosition = input.selectionStart
    const rawValue = rawInput.replace(/\D/g, '')
    const value = rawValue.slice(0, 10)
    let formattedValue = ''
    if (value.length > 6) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(
        3,
        6,
      )}-${value.slice(6)}`
    } else if (value.length > 3) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`
    } else if (value.length > 0) {
      formattedValue = `(${value}`
    }

    let numericCursorPosition = 0
    for (let i = 0; i < cursorPosition; i++) {
      if (/\d/.test(rawInput[i])) {
        numericCursorPosition++
      }
    }

    let adjustedCursorPosition = 0
    let numericCount = 0
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        numericCount++
      }
      if (numericCount >= numericCursorPosition) {
        adjustedCursorPosition = i + 1
        break
      }
    }

    setTimeout(() => {
      input.value = formattedValue
      input.selectionStart = input.selectionEnd = Math.min(
        adjustedCursorPosition,
        formattedValue.length,
      )
    }, 0)

    methods.setValue('phone', formattedValue)
  }

  const handleLogoChange = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setValue('branding.logoFile', file, { shouldValidate: true })
      const url = await uploadFile(file, brand, 'logo')
      setValue('branding.logoUrl', url, { shouldValidate: true })
    } catch (error) {
      setValue('branding.logoFile', null, { shouldValidate: true })
    }
  }

  const handleMediaChange = async e => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const newPreviews = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
    }))
    setMediaPreviews(prev => [...prev, ...newPreviews].slice(0, 4))

    try {
      const companyName = getValues('companyName') || 'unknown'
      const currentFiles = getValues('branding.mediaFiles') || []
      const currentUrls = getValues('branding.mediaUrls') || []

      for (let i = 0; i < files.length && currentFiles.length < 4; i++) {
        const file = files[i]
        const previewIndex = mediaPreviews.length + i

        try {
          const url = await uploadFile(
            file,
            companyName,
            'media',
            currentFiles.length,
          )

          currentFiles.push(file)
          currentUrls.push(url)

          setMediaPreviews(prev =>
            prev.map((p, idx) =>
              idx === previewIndex ? { ...p, url, isUploaded: true } : p,
            ),
          )
        } catch (error) {
          setMediaPreviews(prev =>
            prev.filter((_, idx) => idx !== previewIndex),
          )
        }
      }

      setValue('branding.mediaFiles', currentFiles.slice(0, 4), {
        shouldValidate: true,
      })
      setValue('branding.mediaUrls', currentUrls.slice(0, 4), {
        shouldValidate: true,
      })
    } catch (error) {
      console.error('Media upload error:', error)
    }
  }

  // Remove media file
  const removeMediaFile = index => {
    setMediaPreviews(prev => prev.filter((_, i) => i !== index))
    const updatedFiles = [...getValues('branding.mediaFiles')]
    const updatedUrls = [...getValues('branding.mediaUrls')]

    updatedFiles.splice(index, 1)
    updatedUrls.splice(index, 1)

    setValue('branding.mediaFiles', updatedFiles, { shouldValidate: true })
    setValue('branding.mediaUrls', updatedUrls, { shouldValidate: true })
  }

  const removeLogo = () => {
    setValue('branding.logoFile', null, { shouldValidate: true })
    setValue('branding.logoUrl', null, { shouldValidate: true })
    methods.clearErrors('branding.logoFile')
  }

  const onHandleSubmit = async data => {
    setLoading(true)
    const formData = {
      ...data,
      branding: {
        logoUrl: logoUrl || branding?.logoUrl,
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : branding?.mediaUrls || [],
      },
    }

    if (editMode) {
      console.log('Form submitted:', data)
      console.log('formData:', formData)
      await updateUser({
        variables: {
          input: {
            id: userData?.id,
            businessForm: JSON.stringify(formData),
          },
        },
        refetchQueries: ['ClientQuery'],
      })
      Toast.fire({
        icon: 'success',
        title: 'Business Info Updated Successfully',
      })
      navigate('/profile')
    } else {
      await createBusinessForm({
        variables: {
          form: JSON.stringify(formData),
          brand: brand,
        },
        refetchQueries: ['ClientQuery'],
      })
      const { tokens } = await fetchAuthSession();
      const brandName = tokens?.idToken?.payload?.['custom:manufacturer']
      const { data } = await getManufacture({
        variables: { email: localStorage.getItem('email') },
        fetchPolicy: 'network-only',
      })

      if (
        data?.getManufacturer[0]?.isShopifyConnected === false &&
        data?.getManufacturer[0]?.brand !== 'saddlefitadmin'
      ) {
        navigate(`/shopify-connect`, { replace: true, state: { brandName } })
        onStepChange();
        return
      }
    }

    setLoading(false)
  }

  const handleCancel = () => {
    navigate(-1)
  }

  const copyCompanyAddress = section => {
    const companyAddr = getValues('companyAddress')
    if (section === 'legalBusinessAddress') {
      setValue('legalBusinessAddress', companyAddr, { shouldValidate: true })
    } else {
      setValue(`shippingAddress.${section}`, companyAddr, {
        shouldValidate: true,
      })
    }
  }

  const renderContact = (contactType, contacts) => (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h4 className="text-lg font-medium capitalize font-montserrat">
          {contactType.replace(/([A-Z])/g, ' $1').trim()}
        </h4>

        <div
          onClick={() =>
            addArrayItem(`contacts.${contactType}`, {
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              title: '',
            })
          }
          className="flex items-center gap-2 cursor-pointer"
        >
          <FaPlus />
          <span
            className="text-[#2B364B] font-montserrat font-semibold hover:underline !w-auto cursor-pointer inline-flex items-center justify-center"
            role="button"
          >
            Add {formatName(contactType?.replace(/([A-Z])/g, ' $1')?.trim())} Contact
          </span>
        </div>
      </div>
      {contacts?.map((_, index) => {
        return (
          <div key={index} className="border py-4 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                name={`contacts.${contactType}.${index}.firstName`}
                label="First Name *"
                placeholder="First Name"
                control={control}
                validations={{ required: 'First Name is required' }}
              />
              <FormInput
                name={`contacts.${contactType}[${index}].lastName`}
                label="Last Name *"
                placeholder="Last Name"
                control={control}
                validations={{ required: 'Last Name is required' }}
              />
              <FormInput
                name={`contacts.${contactType}[${index}].email`}
                label="Email Address *"
                placeholder="Email Address"
                control={control}
                validations={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email format',
                  },
                }}
              />
              <FormInput
                // icon={<ClientPhoneIcon className="!text-transparent" />}
                type={'tel'}
                name={`contacts.${contactType}[${index}].phone`}
                label="Phone Number *"
                placeholder={'(850) 621-2434'}
                className="w-full mb-[20px] sm:mb-0"
                validateOnChange={false}
                maxLength={14}
                validations={{
                  required: true,
                  pattern: {
                    value:
                      /^\(?([2-9][0-8][0-9])\)?[-.●\s]?([2-9][0-9]{2})[-.●\s]?([0-9]{4})$/,
                    message: 'Invalid phone number format',
                  },
                }}
                onChange={handlePhoneNumberChange}
              />
              <FormInput
                name={`contacts.${contactType}[${index}].title`}
                label="Title *"
                placeholder="Title"
                control={control}
                className="md:col-span-2"
                validations={{ required: 'Title is required' }}
              />
            </div>
            {contacts.length > 1 && (
              <span
                role="button"
                onClick={() =>
                  removeArrayItem(`contacts.${contactType}`, index)
                }
                className="text-red-500 font-montserrat font-semibold hover:underline !w-auto cursor-pointer inline-flex items-center justify-center py-2"
              >
                Remove Contact
              </span>
            )}
          </div>
        )
      })}
    </div>
  )

  const renderAddress = (addressPath, title) => (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
        <h4 className="text-lg font-medium font-montserrat">{title}</h4>
        <span
          onClick={e => {
            e.preventDefault()
            copyCompanyAddress(addressPath)
          }}
          className="text-[#2B364B] font-montserrat font-semibold hover:underline !w-auto px-5 cursor-pointer inline-flex items-center justify-center"
          role="button"
        >
          Copy Company Address
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          name={`shippingAddress.${addressPath}.street`}
          label="Street Address *"
          placeholder="Street Address"
          control={control}
          validations={{ required: 'Street Address is required' }}
        />
        <FormInput
          name={`shippingAddress.${addressPath}.city`}
          label="City"
          placeholder="City *"
          control={control}
          validations={{ required: 'City is required' }}
        />
        <FormInput
          name={`shippingAddress.${addressPath}.state`}
          label="State/Province *"
          placeholder="State/Province"
          control={control}
          validations={{ required: 'State/Province is required' }}
        />
        <FormInput
          name={`shippingAddress.${addressPath}.zipCode`}
          label="ZIP/Postal Code *"
          placeholder="ZIP/Postal Code"
          control={control}
          validations={{ required: 'ZIP/Postal Code is required' }}
        />
        <FormInput
          name={`shippingAddress.${addressPath}.country`}
          label="Country *"
          placeholder="Country"
          control={control}
          className="md:col-span-2"
          validations={{ required: 'Country is required' }}
        />
      </div>
    </div>
  )

  const renderHoursOfOperation = () => {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ]

    return (
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4 font-montserrat">
          Hours of Operation
        </h3>
        <Controller
          name="hoursOfOperation"
          control={control}
          rules={{ validate: validateHoursOfOperation }}
          render={({ field }) => (
            <>
              {days.map(day => {
                const dayHours = watch(`hoursOfOperation.${day}`)

                return (
                  <div
                    key={day}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-3"
                  >
                    <div className="w-28 capitalize font-medium font-montserrat">
                      {day}
                    </div>
                    <label className="flex items-center gap-2 font-montserrat">
                      <input
                        type="checkbox"
                        checked={dayHours.closed}
                        onChange={e => {
                          const newValue = { ...field.value }
                          newValue[day] = {
                            ...newValue[day],
                            closed: e.target.checked,
                            open: e.target.checked ? '' : newValue[day].open,
                            close: e.target.checked ? '' : newValue[day].close,
                          }
                          field.onChange(newValue)
                        }}
                        className="h-5 w-5"
                      />
                      Closed
                    </label>
                    {!dayHours.closed && (
                      <div className="flex items-center gap-2 sm:gap-4">
                        <input
                          type="time"
                          value={dayHours.open || ''}
                          onChange={e => {
                            const newValue = { ...field.value }
                            newValue[day] = {
                              ...newValue[day],
                              open: e.target.value,
                            }
                            field.onChange(newValue)
                          }}
                          className="border p-2 rounded"
                        />
                        <span className="font-montserrat">to</span>
                        <input
                          type="time"
                          value={dayHours.close || ''}
                          onChange={e => {
                            const newValue = { ...field.value }
                            newValue[day] = {
                              ...newValue[day],
                              close: e.target.value,
                            }
                            field.onChange(newValue)
                          }}
                          className="border p-2 rounded"
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        />
        {formState.errors.hoursOfOperation && (
          <p className="text-red-500 text-sm mt-2 font-montserrat">
            {formState.errors.hoursOfOperation.message}
          </p>
        )}
      </div>
    )
  }

  const renderMediaFiles = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {mediaPreviews.map((preview, index) => (
        <div
          key={index}
          className="relative h-[90px] w-[140px] rounded-lg border-2 border-solid border-gray-300"
        >
          {preview.type === 'image' ? (
            <img
              src={preview.url}
              alt={`Media ${index + 1}`}
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <video className="w-full h-full object-cover rounded-lg">
              <source
                src={preview.url}
                type={preview.type === 'video' ? 'video/mp4' : ''}
              />
            </video>
          )}

          {!preview.isUploaded && preview.file && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="text-white text-xs">
                {progress.media[index] || 0}%
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={() => removeMediaFile(index)}
            className="absolute top-0 right-0 p-1 bg-[#2B364B] rounded-full text-white flex items-center justify-center"
          >
            <CloseIcon className="h-3 w-3 cursor-pointer" />
          </button>
        </div>
      ))}

      {mediaPreviews.length < 4 && (
        <div className="relative h-[90px] w-[140px] rounded-lg border-2 border-dashed border-gray-300">
          <label className="w-[140px] h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
            <input
              type="file"
              className="hidden"
              multiple
              accept={acceptedTypes.media.join(',')}
              onChange={handleMediaChange}
            />
            <ImagePlus className="h-8 w-8 text-gray-400" />
            <span className="text-xs text-gray-500 mt-1 font-montserrat">
              {mediaPreviews.length}/4
            </span>
          </label>
        </div>
      )}
    </div>
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onHandleSubmit)} className=" min-h-screen">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white">
            <div className="space-y-8">
              {/* Form Review */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Form Review
                </h2>
                <div className="bg-gray-50 px-4 py-4 rounded border">
                  <p className="text-gray-700 mb-2 font-montserrat">
                    Please complete all sections below to register as a
                    manufacturer.
                  </p>
                  <p className="text-gray-700 font-montserrat">
                    All fields marked with an asterisk (*) are required.
                  </p>
                </div>
              </section>

              {/* Company Information */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Company Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="mb-4">
                    <FormInput
                      name="companyName"
                      label="Company Name *"
                      placeholder="Company name"
                      control={control}
                      validations={{ required: 'Company Name is required' }}
                    />
                  </div>
                  <div className="mb-4">
                    <FormInput
                      name="legalName"
                      label="Legal Name *"
                      placeholder="Full legal business name"
                      control={control}
                      validations={{ required: 'Legal Name is required' }}
                    />
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <FormInput
                      name="companyEmail"
                      label="Company Email Address *"
                      placeholder="company@example.com"
                      control={control}
                      validations={{
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email format',
                        },
                      }}
                    />
                    <p className="text-sm text-gray-600 mt-2 font-montserrat">
                      This email will be used as your login for the
                      administration CMS dashboard
                    </p>
                  </div>
                </div>
              </section>

              {/* Company Address */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Company Address
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    name="companyAddress.street"
                    label="Street Address *"
                    placeholder="Street Address"
                    control={control}
                    validations={{ required: 'Street Address is required' }}
                  />
                  <FormInput
                    name="companyAddress.city"
                    label="City *"
                    placeholder="City"
                    control={control}
                    validations={{ required: 'City is required' }}
                  />
                  <FormInput
                    name="companyAddress.state"
                    label="State/Province *"
                    placeholder="State/Province"
                    control={control}
                    validations={{ required: 'State/Province is required' }}
                  />
                  <FormInput
                    name="companyAddress.zipCode"
                    label="ZIP/Postal Code *"
                    placeholder="ZIP/Postal Code"
                    control={control}
                    validations={{ required: 'ZIP/Postal Code is required' }}
                  />
                  <FormInput
                    name="companyAddress.country"
                    label="Country *"
                    placeholder="Country"
                    control={control}
                    className="md:col-span-2"
                    validations={{ required: 'Country is required' }}
                  />
                </div>
              </section>

              {/* Legal Business Address */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Legal Business Address
                </h2>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <p className="text-gray-600 font-montserrat">
                    Enter your legal business address
                  </p>
                  <span
                    onClick={e => {
                      e.preventDefault()
                      copyCompanyAddress('legalBusinessAddress')
                    }}
                    className="text-[#2B364B] font-montserrat font-semibold hover:underline !w-auto px-5 cursor-pointer inline-flex items-center justify-center"
                    role="button"
                  >
                    Copy Company Address
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    name="legalBusinessAddress.street"
                    label="Street Address *"
                    placeholder="Street Address"
                    control={control}
                    validations={{ required: 'Street Address is required' }}
                  />
                  <FormInput
                    name="legalBusinessAddress.city"
                    label="City *"
                    placeholder="City"
                    control={control}
                    validations={{ required: 'City is required' }}
                  />
                  <FormInput
                    name="legalBusinessAddress.state"
                    label="State/Province *"
                    placeholder="State/Province"
                    control={control}
                    validations={{ required: 'State/Province is required' }}
                  />
                  <FormInput
                    name="legalBusinessAddress.zipCode"
                    label="ZIP/Postal Code"
                    placeholder="ZIP/Postal Code"
                    control={control}
                    validations={{ required: 'ZIP/Postal Code is required' }}
                  />
                  <FormInput
                    name="legalBusinessAddress.country"
                    label="Country *"
                    placeholder="Country"
                    control={control}
                    className="md:col-span-2"
                    validations={{ required: 'Country is required' }}
                  />
                </div>
              </section>

              {/* Subsidiary Brands */}
              <section>
                <div
                  onClick={() => addArrayItem('subsidiaryBrands', '')}
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                >
                  <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                    Subsidiary Brands
                  </h2>
                  <div className="flex items-center gap-2 cursor-pointer">
                    <FaPlus />
                    <span
                      className="text-[#2B364B] font-montserrat font-semibold hover:underline !w-auto cursor-pointer inline-flex items-center justify-center"
                      role="button"
                    >
                      Add Subsidiary Brand
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {watch('subsidiaryBrands')?.map((brand, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <FormInput
                        name={`subsidiaryBrands[${index}]`}
                        label="Brand Name *"
                        placeholder="Brand Name"
                        control={control}
                        className="flex-1"
                        validations={{ required: 'Brand Name is required' }}
                      />
                      {watch('subsidiaryBrands').length > 1 && (
                        <span
                          role="button"
                          onClick={() =>
                            removeArrayItem('subsidiaryBrands', index)
                          }
                          className="text-red-500 font-montserrat font-semibold hover:underline !w-auto cursor-pointer inline-flex items-center justify-center"
                        >
                          Remove
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Office Phone Number */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Office Phone Number
                </h2>
                <div className="max-w-md">
                  <FormInput
                    type={'tel'}
                    name="officePhoneNumber"
                    label="Office Phone Number *"
                    placeholder="Office Phone Number"
                    className="w-full"
                    validateOnChange={false}
                    maxLength={14}
                    validations={{
                      required: true,
                      pattern: {
                        value:
                          /^\(?([2-9][0-8][0-9])\)?[-.●\s]?([2-9][0-9]{2})[-.●\s]?([0-9]{4})$/,
                        message: 'Invalid phone number format',
                      },
                    }}
                    onChange={handlePhoneNumberChange}
                  />
                </div>
              </section>

              {/* Hours of Operation */}
              <section>{renderHoursOfOperation()}</section>

              {/* Contacts */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Add Contacts for Manufacturer
                </h2>
                <p className="text-gray-600 mb-6 font-montserrat">
                  Please provide contact information including First Name, Last
                  Name, Email Address, Phone Number, and Title for each
                  category.
                </p>

                <div className="space-y-6">
                  {renderContact('ownership', watch('contacts.ownership'))}
                  {renderContact('technical', watch('contacts.technical'))}
                  {renderContact(
                    'accountsPayable',
                    watch('contacts.accountsPayable'),
                  )}
                  {renderContact(
                    'accountsReceivable',
                    watch('contacts.accountsReceivable'),
                  )}
                </div>
              </section>

              {/* Shipping Addresses */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Shipping To / Ship From Address
                </h2>
                <div className="space-y-6">
                  {renderAddress('shipTo', 'Shipping To Address')}
                  {renderAddress('shipFrom', 'Ship From Address')}
                </div>
              </section>

              {/* Types of Saddles */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Types of Saddles
                </h2>
                <p className="text-gray-600 mb-4 font-montserrat">
                  Multiple Choice – Same as our Saddle Disciplines on
                  SaddleFit.io
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {saddleDisciplines.map(discipline => (
                    <label
                      key={discipline}
                      className="flex items-center gap-2 font-montserrat"
                    >
                      <Controller
                        name="saddleTypes"
                        control={control}
                        rules={{ validate: validateSaddleTypes }}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value.includes(discipline)}
                            onChange={e => {
                              const newValue = e.target.checked
                                ? [...field.value, discipline]
                                : field.value.filter(val => val !== discipline)
                              field.onChange(newValue)
                            }}
                            className="h-5 w-5"
                          />
                        )}
                      />
                      {discipline}
                    </label>
                  ))}
                </div>
                {formState.errors.saddleTypes && (
                  <p className="text-red-500 text-sm mt-2 font-montserrat">
                    {formState.errors.saddleTypes.message}
                  </p>
                )}
                {watch('saddleTypes').includes('Other') && (
                  <div className="mt-4 max-w-md">
                    <FormInput
                      name="otherSaddleType"
                      placeholder="Please specify other saddle type"
                      control={control}
                      validations={{
                        validate: validateOtherSaddleType,
                      }}
                    />
                  </div>
                )}
              </section>

              {/* Lead Times */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Lead Times
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Production Lead Times */}
                  <div className="mb-6">
                    <Controller
                      name="leadTimes.production"
                      control={control}
                      rules={{ required: 'Please select production lead time' }}
                      render={({ field }) => (
                        <>
                          <label className="block mb-2 font-medium font-montserrat">
                            Production Saddles
                          </label>
                          <div className="space-y-2">
                            {leadTimeOptions.map(option => (
                              <label
                                key={`production-${option}`}
                                className="flex items-center gap-2 font-montserrat"
                              >
                                <input
                                  type="radio"
                                  checked={field.value === option}
                                  onChange={() => field.onChange(option)}
                                  className="h-5 w-5"
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    />
                  </div>

                  {/* Custom Lead Times */}
                  <div className="mb-6">
                    <Controller
                      name="leadTimes.custom"
                      control={control}
                      rules={{ required: 'Please select custom lead time' }}
                      render={({ field }) => (
                        <>
                          <label className="block mb-2 font-medium font-montserrat">
                            Custom Saddles
                          </label>
                          <div className="space-y-2">
                            {leadTimeOptions.map(option => (
                              <label
                                key={`custom-${option}`}
                                className="flex items-center gap-2 font-montserrat"
                              >
                                <input
                                  type="radio"
                                  checked={field.value === option}
                                  onChange={() => field.onChange(option)}
                                  className="h-5 w-5"
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    />
                  </div>
                </div>

                {/* Show errors if any */}
                {(formState.errors.leadTimes?.production ||
                  formState.errors.leadTimes?.custom) && (
                    <p className="text-red-500 text-sm mt-2 font-montserrat">
                      {formState.errors.leadTimes?.production?.message ||
                        formState.errors.leadTimes?.custom?.message}
                    </p>
                  )}
              </section>

              {/* Policies */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Policies
                </h2>

                {/* Shipping Policy */}
                <div className="mb-6">
                  <Controller
                    name="policies.shipsQuickly"
                    control={control}
                    rules={{ required: 'Please select shipping policy' }}
                    render={({ field }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          Shipping Policy
                        </label>
                        <p className="text-sm text-gray-600 mb-3 font-montserrat">
                          How quickly do you ship orders once saddle is
                          completed?
                        </p>
                        <div className="space-y-2">
                          {[
                            'same-day',
                            '1-2-days',
                            '3-5-days',
                            '1-week',
                            '2-weeks',
                          ].map(option => (
                            <label
                              key={`shipping-${option}`}
                              className="flex items-center gap-2 font-montserrat"
                            >
                              <input
                                type="radio"
                                checked={field.value === option}
                                onChange={() => field.onChange(option)}
                                className="h-5 w-5"
                              />
                              {option === 'same-day' && 'Same day'}
                              {option === '1-2-days' && '1-2 business days'}
                              {option === '3-5-days' && '3-5 business days'}
                              {option === '1-week' && '1 week'}
                              {option === '2-weeks' && '2+ weeks'}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {formState.errors.policies?.shipsQuickly && (
                    <p className="text-red-500 text-sm mt-2 font-montserrat">
                      {formState.errors.policies.shipsQuickly.message}
                    </p>
                  )}
                </div>

                {/* Return Policy */}
                <div className="mb-6">
                  <Controller
                    name="policies.acceptsReturns"
                    control={control}
                    rules={{ required: 'Please select return policy' }}
                    render={({ field }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          Return Policy
                        </label>
                        <p className="text-sm text-gray-600 mb-3 font-montserrat">
                          What are the Terms and Conditions?
                        </p>
                        <div className="space-y-2 mb-4">
                          {['yes', 'conditional', 'no'].map(option => (
                            <label
                              key={`returns-${option}`}
                              className="flex items-center gap-2 font-montserrat"
                            >
                              <input
                                type="radio"
                                checked={field.value === option}
                                onChange={() => field.onChange(option)}
                                className="h-5 w-5"
                              />
                              {option === 'yes' && 'Yes, we accept returns'}
                              {option === 'conditional' &&
                                'Yes, with conditions'}
                              {option === 'no' && 'No returns accepted'}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {formState.errors.policies?.acceptsReturns && (
                    <p className="text-red-500 text-sm mt-2 font-montserrat">
                      {formState.errors.policies.acceptsReturns.message}
                    </p>
                  )}

                  <FormInput
                    name="policies.returnsTerms"
                    label="Return Policy Terms *"
                    placeholder="Please provide detailed return policy terms and conditions..."
                    multiline
                    rows={4}
                    control={control}
                    validations={{
                      required:
                        watch('policies.acceptsReturns') !== 'no'
                          ? 'Please provide return policy terms'
                          : false,
                    }}
                  />
                </div>

                {/* Warranty Policy */}
                <div className="mb-6">
                  <Controller
                    name="policies.hasWarranty"
                    control={control}
                    rules={{ required: 'Please select warranty policy' }}
                    render={({ field }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          Warranty Terms and Conditions
                        </label>
                        <p className="text-sm text-gray-600 mb-3 font-montserrat">
                          What are the Terms and Conditions?
                        </p>
                        <div className="space-y-2 mb-4">
                          {['yes', 'limited', 'no'].map(option => (
                            <label
                              key={`warranty-${option}`}
                              className="flex items-center gap-2 font-montserrat"
                            >
                              <input
                                type="radio"
                                checked={field.value === option}
                                onChange={() => field.onChange(option)}
                                className="h-5 w-5"
                              />
                              {option === 'yes' && 'Yes, we provide warranty'}
                              {option === 'limited' && 'Limited warranty only'}
                              {option === 'no' && 'No warranty provided'}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {formState.errors.policies?.hasWarranty && (
                    <p className="text-red-500 text-sm mt-2 font-montserrat">
                      {formState.errors.policies.hasWarranty.message}
                    </p>
                  )}

                  <FormInput
                    name="policies.warrantyTerms"
                    label="Warranty Terms *"
                    placeholder="Please provide detailed warranty terms and conditions..."
                    multiline
                    rows={4}
                    control={control}
                    validations={{
                      required:
                        watch('policies.hasWarranty') !== 'no'
                          ? 'Please provide warranty terms'
                          : false,
                    }}
                  />
                </div>

                {/* Cancellation Policy */}
                <div className="mb-6">
                  <Controller
                    name="policies.allowsCancellation"
                    control={control}
                    rules={{ required: 'Please select cancellation policy' }}
                    render={({ field }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          Order Cancellation Policy
                        </label>
                        <p className="text-sm text-gray-600 mb-3 font-montserrat">
                          What are the Terms and Conditions?
                        </p>
                        <div className="space-y-2 mb-4">
                          {['yes', 'timeframe', 'no'].map(option => (
                            <label
                              key={`cancellation-${option}`}
                              className="flex items-center gap-2 font-montserrat"
                            >
                              <input
                                type="radio"
                                checked={field.value === option}
                                onChange={() => field.onChange(option)}
                                className="h-5 w-5"
                              />
                              {option === 'yes' && 'Yes, cancellations allowed'}
                              {option === 'timeframe' &&
                                'Yes, within specific timeframe'}
                              {option === 'no' && 'No cancellations allowed'}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {formState.errors.policies?.allowsCancellation && (
                    <p className="text-red-500 text-sm mt-2 font-montserrat">
                      {formState.errors.policies.allowsCancellation.message}
                    </p>
                  )}

                  <FormInput
                    name="policies.cancellationTerms"
                    label="Cancellation Terms *"
                    placeholder="Please provide detailed cancellation policy terms and conditions..."
                    multiline
                    rows={4}
                    control={control}
                    validations={{
                      required:
                        watch('policies.allowsCancellation') !== 'no'
                          ? 'Please provide cancellation terms'
                          : false,
                    }}
                  />
                </div>
              </section>

              {/* Branding */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 font-montserrat">
                  Branding
                </h2>

                <div className="mb-6">
                  <Controller
                    name="branding.logoFile"
                    control={control}
                    rules={{
                      validate: {
                        required: file =>
                          !!file || !!logoUrl || 'Please upload a logo file',
                        validType: file =>
                          !file ||
                          acceptedTypes.logo.includes(file.type) ||
                          'Please upload a valid image (JPEG, PNG, GIF, WEBP)',
                        maxSize: file =>
                          !file ||
                          file.size <= MAX_IMAGE_SIZE ||
                          'File size must be less than 5MB',
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          High-Resolution Logo
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div
                            className={`relative h-[90px] w-[140px] rounded-lg border-2 ${field.value ? 'border-solid' : 'border-dashed'
                              } border-gray-300`}
                          >
                            {logoUrl || field.value ? (
                              <div className="max-w-[140px] w-full h-[90px]">
                                {field.value ? (
                                  <img
                                    src={
                                      typeof field.value === 'string'
                                        ? field.value
                                        : URL.createObjectURL(field.value)
                                    }
                                    alt="Logo preview"
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                ) : (
                                  <img
                                    src={logoUrl}
                                    alt="Logo preview"
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                )}
                                {uploading.logo && (
                                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                    <span className="text-white text-sm">
                                      {progress.logo}%
                                    </span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={removeLogo}
                                  className="absolute top-0 right-0 p-1 bg-[#2B364B] rounded-full text-white flex items-center justify-center"
                                >
                                  <CloseIcon className="h-3 w-3 cursor-pointer" />
                                </button>
                              </div>
                            ) : (
                              <label className="w-[140px] h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept={acceptedTypes.logo.join(',')}
                                  onChange={e => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                      handleLogoChange(e)
                                      field.onChange(file)
                                    }
                                  }}
                                />
                                <ImagePlus className="h-8 w-8 text-gray-400" />
                              </label>
                            )}
                          </div>
                        </div>
                        {fieldState.error && (
                          <p className="text-red-500 text-sm mt-2 font-montserrat">
                            {fieldState.error.message}
                          </p>
                        )}
                        {uploadErrors.logo && !fieldState.error && (
                          <p className="text-red-500 text-sm mt-2 font-montserrat">
                            {uploadErrors.logo}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>

                {/* Media Files Upload Section */}
                <div className="mb-6">
                  <Controller
                    name="branding.mediaFiles"
                    control={control}
                    rules={{
                      validate: {
                        required: files =>
                          !files ||
                          files.length > 0 ||
                          'Please upload at least one media file',
                        maxCount: files =>
                          !files ||
                          files.length <= 4 ||
                          'Maximum 4 media files allowed',
                        validTypes: files =>
                          !files ||
                          files.every(file =>
                            acceptedTypes.media.includes(file.type),
                          ) ||
                          'Only images (JPEG, PNG, GIF, WEBP) and videos (MP4, WEBM) are allowed',
                        maxSize: files =>
                          !files ||
                          files.every(file =>
                            file.type.startsWith('video/')
                              ? file.size <= MAX_VIDEO_SIZE
                              : file.size <= MAX_IMAGE_SIZE,
                          ) ||
                          'Videos max 50MB, Images max 5MB',
                      },
                    }}
                    render={({ _, fieldState }) => (
                      <>
                        <label className="block mb-2 font-medium font-montserrat">
                          Photos / Videos to Use (Max 4)
                        </label>
                        {renderMediaFiles()}
                        {fieldState.error && (
                          <p className="text-red-500 text-sm mt-2 font-montserrat">
                            {fieldState.error.message}
                          </p>
                        )}
                        {uploadErrors.media && !fieldState.error && (
                          <p className="text-red-500 text-sm mt-2 font-montserrat">
                            {uploadErrors.media}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
              </section>

              {/* Submit Button */}
              <div className="py-6 flex gap-4">
                {editMode && (
                  <Button
                    type="button"
                    primaryOutline
                    onClick={handleCancel}
                    disabled={createManufactureLoading || updateUserLoading}
                    className={`create-saddle-btn !w-auto px-6 cursor-pointer`}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  loading={createManufactureLoading || updateUserLoading}
                  disabled={createManufactureLoading || updateUserLoading}
                  className={`create-saddle-btn !w-auto px-10 cursor-pointer`}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default OnBoardingForm
